import { getCookie } from '@/helpers/cookieMetods';
import type { EntityId, OrderStatus } from '../../types/common';
import type {
  AdminOrderApiMap,
  ApiStatusResponse,
  CartItem,
  MerchListItem,
  OrderApiMap,
  OrderUpdatePayload,
} from '../../types/orders';
import { apiUrl, parseJsonOrThrow } from '../http';

export interface AddCartResponse extends ApiStatusResponse {
  order_id?: EntityId;
}

type CartApiRow = Omit<CartItem, 'orderId'>;

/**
 * Maps `order.php?action=out` wire JSON to `CartItem[]`. Handles both the new
 * flat-array shape (`order_id` on each row) and the legacy object-map shape
 * (`{ order_id, row1, row2, … }`) used by the mock server / older API.
 */
const normalizeCartItems = (result: unknown): CartItem[] => {
  if (result === null || result === undefined || typeof result !== 'object') {
    return [];
  }

  if (Array.isArray(result)) {
    return result.reduce<CartItem[]>((acc, curr) => {
      if (curr && typeof curr === 'object' && !Array.isArray(curr)) {
        const row = curr as CartApiRow & { order_id?: EntityId };
        return [...acc, { ...row, orderId: row.order_id }];
      }
      return acc;
    }, []);
  }

  const r = result as Record<string, unknown> & { order_id?: EntityId };
  return Object.values(r).reduce<CartItem[]>((acc, curr) => {
    if (curr && typeof curr === 'object' && !Array.isArray(curr)) {
      return [...acc, { ...(curr as CartApiRow), orderId: r.order_id }];
    }
    return acc;
  }, []);
};

export const getCartItems = (
  user_id: EntityId | undefined
): Promise<CartItem[]> => {
  const token = getCookie('_form_token');
  return fetch(apiUrl('/php/order.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'out', user_id, token }),
  })
    .then((response) => parseJsonOrThrow<unknown>(response))
    .then(normalizeCartItems);
};

export const addCartItem = (
  user_id: EntityId | undefined,
  variant_id: EntityId | undefined
): Promise<AddCartResponse> => {
  const token = getCookie('_form_token');
  return fetch(apiUrl('/php/order.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'add',
      variant_id,
      user_id,
      token,
    }),
  }).then((response) => parseJsonOrThrow<AddCartResponse>(response));
};

export const removeCartItem = (
  user_id: EntityId | undefined,
  variant_id: EntityId | undefined
): Promise<ApiStatusResponse> => {
  const token = getCookie('_form_token');
  return fetch(apiUrl('/php/order.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'variant_del',
      variant_id,
      user_id,
      token,
    }),
  }).then((response) => parseJsonOrThrow<ApiStatusResponse>(response));
};

export const changeOrderStatus = (
  user_id: EntityId | undefined,
  status: OrderStatus,
  data: unknown,
  orderId: EntityId | undefined
): Promise<ApiStatusResponse> => {
  const token = getCookie('_form_token');
  return fetch(apiUrl('/php/order.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'change_status',
      status,
      user_id,
      delivery: JSON.stringify(data),
      token,
      order_id: orderId,
    }),
  }).then((response) => parseJsonOrThrow<ApiStatusResponse>(response));
};

export const getOrdersList = (
  user_id: EntityId | undefined,
  {
    status = '',
    sort = 'DESC',
    start = 0,
    limit = 10,
  }: {
    status?: OrderStatus;
    sort?: 'ASC' | 'DESC';
    start?: number;
    limit?: number;
  } = {}
): Promise<OrderApiMap> => {
  const token = getCookie('_form_token');
  return fetch(apiUrl('/php/order.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'orders_list',
      user_id,
      ...(status && { status }),
      sort,
      start,
      limit,
      token,
    }),
  }).then((response) => parseJsonOrThrow<OrderApiMap>(response));
};

export const getMerchList = (
  userCode: string | undefined
): Promise<MerchListItem[]> => {
  const token = getCookie('_form_token');
  return fetch(apiUrl('/php/merch.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ usercode: userCode, token }),
  }).then((response) => parseJsonOrThrow<MerchListItem[]>(response));
};

export const getOrder = (
  user_id: EntityId | undefined,
  order_id: EntityId | undefined
): Promise<AdminOrderApiMap> => {
  const token = getCookie('_form_token');
  return fetch(apiUrl('/php/order.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'out',
      status: 'order',
      user_id,
      order_id,
      token,
    }),
  }).then((response) => parseJsonOrThrow<AdminOrderApiMap>(response));
};

export const deleteOrder = (
  user_id: EntityId | undefined,
  _admin_id: EntityId | undefined,
  order_id: EntityId | undefined
): Promise<ApiStatusResponse> => {
  const token = getCookie('_form_token');
  return fetch(apiUrl('/php/order.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'order_del',
      user_id,
      order_id,
      token,
    }),
  }).then((response) => parseJsonOrThrow<ApiStatusResponse>(response));
};

export const updateOrderItems = (
  admin_id: EntityId | undefined,
  order: OrderUpdatePayload
): Promise<ApiStatusResponse> => {
  const token = getCookie('_form_token');
  return fetch(apiUrl('/php/order.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'edit',
      admin_id,
      token,
      order,
    }),
  }).then((res) => parseJsonOrThrow<ApiStatusResponse>(res));
};

export const manualAddMerch = (
  name: string,
  usercode: string | undefined
): Promise<ApiStatusResponse> => {
  const token = getCookie('_form_token');
  return fetch(apiUrl('/php/manual_add_merch.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, usercode, token }),
  }).then((response) => parseJsonOrThrow<ApiStatusResponse>(response));
};

export const deleteMerch = (
  order: string,
  variant: string
): Promise<ApiStatusResponse> => {
  const token = getCookie('_form_token');
  return fetch(apiUrl('/php/edit_merch.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ order, variant, token }),
  }).then((response) => parseJsonOrThrow<ApiStatusResponse>(response));
};
